import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState, useMemo, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element'
import { SectorSelect } from '../../../../components/Select/SectorSelect'
import { AuthorizerMultiSelect } from '../../../../components/Select/AuthorizerMultiSelect'
import type { AuthorizerOption } from '../../../../components/Select'
import { Textarea } from '../../../../components'
import { Button } from '../../../../components/Button'
import { Input } from '../../../../components'
import { BaseModal } from '../../../../components/Modal/BaseModal'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../components/Forsm/form'
import { processFormSchema, type CreateProcessoRequest, type ProcessFormValues } from './schemas'
import type { Setor } from '../../../../models'
import { isEquipe, getSetorTypeLabel } from '../../../../models'
import {
  RiFilePaper2Line,
  RiAddLine,
  RiDeleteBin6Line,
  RiLinksLine,
  RiAttachmentLine,
  RiDownloadLine,
} from 'react-icons/ri'
import { HiOutlineBuildingOffice, HiClock } from 'react-icons/hi2'
import { FaUserShield, FaLock, FaBook, FaUsers } from 'react-icons/fa'
import { HiOfficeBuilding, HiTemplate } from 'react-icons/hi'
import { authService } from '../../../../services/auth'
import { api } from '../../../../services/axios'
import { processRichTextForDisplay } from '../../../../utils/formatter'

// Sistema de Templates
import {
  TemplateSelector,
  TemplatePreview,
  templatesDisponiveis,
  getTemplateById,
} from '../../../templates'
import type { TemplateSchema } from '../../../templates'

// Helper para serializar objetos/arrays em FormData usando notação compatível com PHP
const appendNestedFormData = (formData: FormData, value: unknown, parentKey: string) => {
  if (value === null || value === undefined) {
    return
  }

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      appendNestedFormData(formData, item, `${parentKey}[${index}]`)
    })
    return
  }

  if (value instanceof File) {
    formData.append(parentKey, value)
    return
  }

  if (typeof value === 'object') {
    Object.entries(value as Record<string, unknown>).forEach(([key, nestedValue]) => {
      appendNestedFormData(formData, nestedValue, `${parentKey}[${key}]`)
    })
    return
  }

  formData.append(parentKey, String(value))
}

// Interfaces para tipar os dados
interface Autorizacao {
  autorizador_fk: number
  ordem: number | null
  active?: boolean
}

interface LinkProcesso {
  link: string
  titulo?: string | undefined
}

interface AnexoProcesso {
  anexo: string
  nome_anexo?: string
}

interface InitialProcessData {
  nome_processo?: string
  tipo?: string
  prioridade_baixa?: number
  prioridade_media?: number
  prioridade_alta?: number
  prioridade_urgente?: number
  setor_processo_fk?: number
  descricao?: string
  instrucoes?: string
  links?: LinkProcesso[] | string
  autorizacoes?: Autorizacao[]
  anexos?: AnexoProcesso[]
  gestor_demandante?: boolean
  gestor_demandado?: boolean
  executivo_demandante?: boolean
  executivo_demandado?: boolean
  equipe?: boolean // Pode ser aberto por equipe
  template_schema?: TemplateSchema | null // Schema do template selecionado
}

interface ProcessesFormProps {
  setores: Setor[]
  onSubmit: (data: CreateProcessoRequest | FormData) => Promise<void>
  onCancel?: () => void
  isLoading?: boolean
  isLoadingSetores?: boolean
  initialData?: InitialProcessData
  isEdit?: boolean
}

// Componente para item de autorizador arrastável
interface DraggableAutorizerItemProps {
  aut: Autorizacao
  index: number
  autorizador: AuthorizerOption
  isSequencial: boolean
  onMoveUp: (index: number) => void
  onMoveDown: (index: number) => void
  onRemove: (index: number) => void
  onReorder: (startIndex: number, endIndex: number) => void
  totalItems: number
  ordemVisual?: number // Ordem visual considerando gestores/executivos
}

const DraggableAutorizerItem = ({
  aut,
  index,
  autorizador,
  isSequencial,
  onMoveUp,
  onMoveDown,
  onRemove,
  onReorder,
  totalItems,
  ordemVisual,
}: DraggableAutorizerItemProps) => {
  const elementRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isDraggedOver, setIsDraggedOver] = useState(false)

  useEffect(() => {
    const element = elementRef.current
    if (!element || !isSequencial) return

    return combine(
      draggable({
        element,
        getInitialData: () => ({ index, autorizadorId: aut.autorizador_fk }),
        onDragStart: () => setIsDragging(true),
        onDrop: () => setIsDragging(false),
      }),
      dropTargetForElements({
        element,
        getData: () => ({ index }),
        onDragEnter: () => setIsDraggedOver(true),
        onDragLeave: () => setIsDraggedOver(false),
        onDrop: ({ source }) => {
          setIsDraggedOver(false)
          const startIndex = source.data.index as number
          if (startIndex !== index) {
            onReorder(startIndex, index)
          }
        },
      })
    )
  }, [index, aut.autorizador_fk, isSequencial, onReorder])

  const Icon = autorizador.tipo === 'setor' ? HiOfficeBuilding : FaUsers

  return (
    <motion.div
      ref={elementRef}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className={`flex items-center justify-between p-3 rounded-lg transition-all ${
        isDragging
          ? 'bg-blue-100 dark:bg-blue-900/50 shadow-lg scale-105'
          : isDraggedOver
            ? 'bg-blue-50 dark:bg-blue-900/30 border-2 border-blue-300 dark:border-blue-600'
            : 'bg-gray-50 dark:bg-neutral-700'
      } ${isSequencial ? 'cursor-move' : ''}`}
      style={{
        transform: isDragging ? 'rotate(2deg)' : 'none',
        zIndex: isDragging ? 1000 : 'auto',
      }}
    >
      <div className='flex items-center gap-3'>
        {isSequencial && (
          <div className='flex flex-col gap-1'>
            <button
              type='button'
              onClick={() => onMoveUp(index)}
              disabled={index === 0}
              className='text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed'
            >
              ▲
            </button>
            <button
              type='button'
              onClick={() => onMoveDown(index)}
              disabled={index === totalItems - 1}
              className='text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed'
            >
              ▼
            </button>
          </div>
        )}

        {isSequencial && (
          <div className='w-8 h-8 rounded-full bg-blue-600 dark:bg-blue-500 text-white flex items-center justify-center font-semibold'>
            {ordemVisual || aut.ordem}
          </div>
        )}

        <Icon
          className={`w-8 h-8 p-1.5 rounded-full ${
            autorizador.tipo === 'setor'
              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
              : 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
          }`}
        />

        <div>
          <div className='flex items-center gap-2'>
            <p className='text-sm font-medium text-gray-900 dark:text-gray-100'>
              {autorizador.nome_setor}
            </p>
            <span
              className={`text-xs px-1.5 py-0.5 rounded ${
                autorizador.tipo === 'setor'
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                  : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
              }`}
            >
              {autorizador.tipo === 'setor' ? 'Setor' : 'Equipe'}
            </span>
          </div>
          {autorizador.chefe && (
            <p className='text-xs text-gray-600 dark:text-gray-400'>
              Chefe: {autorizador.chefe.name}
            </p>
          )}
        </div>

        {isSequencial && (
          <div className='ml-4 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1'>
            <span className='select-none'>⋮⋮</span>
            <span className='select-none'>Arraste para reordenar</span>
          </div>
        )}
      </div>

      <Button
        type='button'
        variant='ghost'
        size='sm'
        onClick={() => onRemove(index)}
        className='text-red-600 hover:text-red-700 hover:bg-red-50'
      >
        <RiDeleteBin6Line className='w-4 h-4' />
      </Button>
    </motion.div>
  )
}

export const ProcessesForm = ({
  setores,
  onSubmit,
  onCancel,
  isLoading = false,
  isLoadingSetores = false,
  initialData,
  isEdit = false,
}: ProcessesFormProps) => {
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedAutorizadorIds, setSelectedAutorizadorIds] = useState<string[]>([]) // Mudou para array
  const [linkInput, setLinkInput] = useState<string>('')
  const [linkTitleInput, setLinkTitleInput] = useState<string>('') // ✅ Título do link
  const [anexos, setAnexos] = useState<File[]>([])
  const [anexosToRemove, setAnexosToRemove] = useState<string[]>([]) // Anexos existentes marcados para remoção
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [pendingData, setPendingData] = useState<ProcessFormValues | null>(null)

  // Estados dos switches de autorização (gestores e executivos)
  const [gestorDemandanteAtivo, setGestorDemandanteAtivo] = useState(false)
  const [gestorDemandadoAtivo, setGestorDemandadoAtivo] = useState(false)
  const [executivoDemandanteAtivo, setExecutivoDemandanteAtivo] = useState(false)
  const [executivoDemandadoAtivo, setExecutivoDemandadoAtivo] = useState(false)

  // Estados para sistema de templates
  const [usaTemplate, setUsaTemplate] = useState(false)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const [templateSchema, setTemplateSchema] = useState<TemplateSchema | null>(null)
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false)

  // Valores iniciais para rastrear mudanças no template
  const usaTemplateInicial = useMemo(() => !!initialData?.template_schema, [initialData?.template_schema])
  const selectedTemplateIdInicial = useMemo(() => initialData?.template_schema?.template_id ?? null, [initialData?.template_schema])

  // Sincronizar switches com initialData ao editar
  useEffect(() => {
    if (initialData) {
      setGestorDemandanteAtivo(!!initialData.gestor_demandante)
      setGestorDemandadoAtivo(!!initialData.gestor_demandado)
      setExecutivoDemandanteAtivo(!!initialData.executivo_demandante)
      setExecutivoDemandadoAtivo(!!initialData.executivo_demandado)
      
      // Sincronizar estados de template
      if (initialData.template_schema) {
        setUsaTemplate(true)
        setSelectedTemplateId(initialData.template_schema.template_id)
        setTemplateSchema(initialData.template_schema)
      }
    }
  }, [initialData])

  // Handler para download de anexos autenticado
  const handleDownloadAttachment = async (anexoObj: AnexoProcesso) => {
    let link: HTMLAnchorElement | null = null;
    let downloadUrl: string | null = null;

    try {
      // Garantir que Sanctum esteja inicializado
      await authService.ensureSanctumInitialized()

      const anexoPath = anexoObj.anexo
      let endpoint: string

      // Determinar o endpoint correto baseado no caminho
      if (anexoPath.includes('processos/anexos/')) {
        const filename = anexoPath.split('/').pop() || anexoPath
        endpoint = `/storage/processos/anexos/${encodeURIComponent(filename)}`
      } else if (anexoPath.includes('anexos/')) {
        const filename = anexoPath.split('/').pop() || anexoPath
        endpoint = `/storage/anexos/${encodeURIComponent(filename)}`
      } else {
        const filename = anexoPath.split('/').pop() || anexoPath
        endpoint = `/storage/processos/anexos/${encodeURIComponent(filename)}`
      }

      // Fazer requisição autenticada para baixar o blob
      const response = await api.get(endpoint, {
        responseType: 'blob',
        headers: {
          Accept: '*/*',
        },
      })

      // Criar URL do blob e fazer download
      const blob = response.data
      downloadUrl = URL.createObjectURL(blob)

      link = document.createElement('a')
      link.href = downloadUrl
      link.download = anexoObj.nome_anexo || 'anexo'
      document.body.appendChild(link)
      link.click()

      // Aguardar um pouco antes de remover para garantir que o download iniciou
      await new Promise(resolve => setTimeout(resolve, 100))

    } catch (error) {
      console.error('Erro ao fazer download do anexo:', error)
      // Fallback: tentar método antigo
      const baseUrl = import.meta.env.VITE_API_URL
      const anexoPath = anexoObj.anexo
      const anexoUrl = anexoPath?.startsWith('http') ? anexoPath : `${baseUrl}/storage/${anexoPath}`
      window.open(anexoUrl, '_blank')
    } finally {
      // Cleanup seguro: remove() não lança erro se o elemento não estiver no DOM
      link?.remove();
      
      // Limpar o objeto URL
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    }
  }

  // Setup do auto-scroll para drag and drop
  useEffect(() => {
    return autoScrollForElements({
      element: document.documentElement,
    })
  }, [])

  // Mapear autorizações do backend para o formato do formulário
  const mapAutorizacoes = () => {
    if (!initialData?.autorizacoes || !Array.isArray(initialData.autorizacoes)) {
      return []
    }

    return initialData.autorizacoes
      .filter((aut: Autorizacao) => aut.active !== false)
      .map((aut: Autorizacao) => ({
        autorizador_fk: aut.autorizador_fk,
        ordem: aut.ordem,
      }))
  }

  // Mapear links do backend para o formato do formulário
  const mapLinks = () => {
    if (!initialData?.links) return []

    // Se vier como string JSON, fazer parse
    if (typeof initialData.links === 'string') {
      try {
        const parsed = JSON.parse(initialData.links)
        return Array.isArray(parsed) ? parsed : []
      } catch {
        return []
      }
    }

    // Se já for array, retornar
    if (Array.isArray(initialData.links)) {
      return initialData.links
    }

    return []
  }

  const autorizacoesIniciais = mapAutorizacoes()
  const linksIniciais = mapLinks()
  const temAutorizacoes = autorizacoesIniciais.length > 0

  const gestorDemandanteInicial = !!initialData?.gestor_demandante
  const gestorDemandadoInicial = !!initialData?.gestor_demandado
  const executivoDemandanteInicial = !!initialData?.executivo_demandante
  const executivoDemandadoInicial = !!initialData?.executivo_demandado

  const hasToggleAuthorizers =
    gestorDemandanteInicial ||
    gestorDemandadoInicial ||
    executivoDemandanteInicial ||
    executivoDemandadoInicial

  const ehSequencial =
    (temAutorizacoes && autorizacoesIniciais.some((aut: Autorizacao) => aut.ordem !== null)) ||
    hasToggleAuthorizers

  const requerAutorizacaoPadrao = temAutorizacoes || hasToggleAuthorizers

  const form = useForm({
    resolver: zodResolver(processFormSchema),
    defaultValues: {
      nome_processo: initialData?.nome_processo || '',
      tipo: initialData?.tipo || '',
      prioridade_baixa: initialData?.prioridade_baixa || 8,
      prioridade_media: initialData?.prioridade_media || 6,
      prioridade_alta: initialData?.prioridade_alta || 4,
      prioridade_urgente: initialData?.prioridade_urgente || 2,
      setor_processo_fk: initialData?.setor_processo_fk?.toString() || '',
      descricao: initialData?.descricao || '',
      instrucoes: initialData?.instrucoes || '',
      links: linksIniciais,
      requer_autorizacao: requerAutorizacaoPadrao,
      autorizacao_sequencial: ehSequencial,
      autorizacoes: autorizacoesIniciais,
      gestor_demandante: gestorDemandanteInicial,
      gestor_demandado: gestorDemandadoInicial,
      executivo_demandante: executivoDemandanteInicial,
      executivo_demandado: executivoDemandadoInicial,
      equipe: initialData?.equipe ?? true, // Por padrão true (pode ser aberto por equipe)
    },
  })

  // Sincronizar switches com initialData ao editar e manter form atualizado
  useEffect(() => {
    setGestorDemandanteAtivo(gestorDemandanteInicial)
    setGestorDemandadoAtivo(gestorDemandadoInicial)
    setExecutivoDemandanteAtivo(executivoDemandanteInicial)
    setExecutivoDemandadoAtivo(executivoDemandadoInicial)

    form.setValue('gestor_demandante', gestorDemandanteInicial, { shouldDirty: false })
    form.setValue('gestor_demandado', gestorDemandadoInicial, { shouldDirty: false })
    form.setValue('executivo_demandante', executivoDemandanteInicial, { shouldDirty: false })
    form.setValue('executivo_demandado', executivoDemandadoInicial, { shouldDirty: false })
    form.setValue('requer_autorizacao', requerAutorizacaoPadrao, { shouldDirty: false })
    form.setValue('autorizacao_sequencial', ehSequencial, { shouldDirty: false })
  }, [
    initialData,
    form,
    gestorDemandanteInicial,
    gestorDemandadoInicial,
    executivoDemandanteInicial,
    executivoDemandadoInicial,
    requerAutorizacaoPadrao,
    ehSequencial,
  ])

  // Converter setores e equipes para opções do AuthorizerSelect
  const autorizadorOptions: AuthorizerOption[] = useMemo(() => {
    // Separar setores e equipes usando helper function para compatibilidade
    const setoresGerais = setores.filter(s => !isEquipe(s))
    const equipes = setores.filter(s => isEquipe(s))

    // Converter para o formato AuthorizerOption
    const setorOpts: AuthorizerOption[] = setoresGerais.map(setor => {
      const opt: AuthorizerOption = {
        value: setor.id.toString(),
        label: setor.nome_setor,
        nome_setor: setor.nome_setor,
        tipo: 'setor' as const,
      }
      if (setor.chefe) {
        opt.chefe = {
          id: setor.chefe.id,
          name: setor.chefe.name,
        }
      }
      return opt
    })

    const equipeOpts: AuthorizerOption[] = equipes.map(equipe => {
      const opt: AuthorizerOption = {
        value: equipe.id.toString(),
        label: equipe.nome_setor,
        nome_setor: equipe.nome_setor,
        tipo: 'equipe' as const,
      }
      if (equipe.chefe) {
        opt.chefe = {
          id: equipe.chefe.id,
          name: equipe.chefe.name,
        }
      }
      return opt
    })

    // Retornar setores primeiro, depois equipes
    return [...setorOpts, ...equipeOpts]
  }, [setores])

  // Converter setores E equipes para opções do SectorSelect (incluir ambos)
  const setorOptions = setores
    .sort((a, b) => {
      // Usar helper function para garantir compatibilidade com 0/1 e true/false
      const aIsEquipe = isEquipe(a)
      const bIsEquipe = isEquipe(b)

      // Setores primeiro, equipes depois
      if (!aIsEquipe && bIsEquipe) return -1
      if (aIsEquipe && !bIsEquipe) return 1
      // Se forem do mesmo tipo, ordenar alfabeticamente
      return a.nome_setor.localeCompare(b.nome_setor)
    })
    .map(setor => ({
      value: setor.id.toString(),
      label: setor.nome_setor,
      nome_setor: setor.nome_setor,
      // Usar helper function para obter o label correto
      descricao: getSetorTypeLabel(setor),
    }))

  // Encontrar setor selecionado e dados do formulário para o resumo
  const selectedSetorId = form.watch('setor_processo_fk')
  const nomeProcesso = form.watch('nome_processo')
  
  // Obter nome do setor selecionado para prefixo visual
  const selectedSetorNome = useMemo(() => {
    if (!selectedSetorId) return null
    const setor = setores.find(s => s.id.toString() === selectedSetorId)
    return setor?.nome_setor || null
  }, [selectedSetorId, setores])
  const descricao = form.watch('descricao')
  const prioridadeBaixa = form.watch('prioridade_baixa')
  const prioridadeMedia = form.watch('prioridade_media')
  const prioridadeAlta = form.watch('prioridade_alta')
  const prioridadeUrgente = form.watch('prioridade_urgente')
  const requerAutorizacao = form.watch('requer_autorizacao')
  const autorizacaoSequencial = form.watch('autorizacao_sequencial')
  const autorizacoesSelecionadas = form.watch('autorizacoes') || []
  const instrucoes = form.watch('instrucoes')
  const linksData = form.watch('links')
  const linksSelecionados = Array.isArray(linksData) ? linksData : []

  // Verificar se há mudanças no formulário
  // Verificar se há mudanças no formulário
  const hasFormChanges = useMemo(() => {
    if (!isEdit) return true // Para novo processo, sempre permitir salvar

    // Verificar mudanças nos campos do formulário usando isDirty do react-hook-form
    const formIsDirty = form.formState.isDirty

    // Verificar mudanças nos anexos
    const hasNewAnexos = anexos.length > 0
    const hasRemovedAnexos = anexosToRemove.length > 0

    // ✅ Verificar mudanças nos links
    const linksAtuais = form.getValues('links') || []
    const linksOriginais = linksIniciais || []
    const hasLinksChanged = JSON.stringify(linksAtuais) !== JSON.stringify(linksOriginais)

    // ✅ Verificar mudanças nas autorizações
    const autorizacoesAtuais = form.getValues('autorizacoes') || []
    const autorizacoesOriginais = autorizacoesIniciais || []
    const hasAutorizacoesChanged =
      JSON.stringify(autorizacoesAtuais) !== JSON.stringify(autorizacoesOriginais)

    // ✅ Verificar mudanças no toggle de autorização
    const requerAutorizacaoAtual = form.getValues('requer_autorizacao')
    const requerAutorizacaoOriginal = requerAutorizacaoPadrao
    const hasRequerAutorizacaoChanged = requerAutorizacaoAtual !== requerAutorizacaoOriginal

    // ✅ Verificar mudanças no toggle de autorização sequencial
    const autorizacaoSequencialAtual = form.getValues('autorizacao_sequencial')
    const autorizacaoSequencialOriginal = ehSequencial
    const hasAutorizacaoSequencialChanged =
      autorizacaoSequencialAtual !== autorizacaoSequencialOriginal

    // ✅ Verificar mudanças nos switches de gestores e executivos
    const gestorDemandanteOriginal = gestorDemandanteInicial
    const gestorDemandadoOriginal = gestorDemandadoInicial
    const executivoDemandanteOriginal = executivoDemandanteInicial
    const executivoDemandadoOriginal = executivoDemandadoInicial

    const hasGestorDemandanteChanged = gestorDemandanteAtivo !== gestorDemandanteOriginal
    const hasGestorDemandadoChanged = gestorDemandadoAtivo !== gestorDemandadoOriginal
    const hasExecutivoDemandanteChanged = executivoDemandanteAtivo !== executivoDemandanteOriginal
    const hasExecutivoDemandadoChanged = executivoDemandadoAtivo !== executivoDemandadoOriginal

    // ✅ Verificar mudanças no template
    const hasUsaTemplateChanged = usaTemplate !== usaTemplateInicial
    const hasSelectedTemplateIdChanged = selectedTemplateId !== selectedTemplateIdInicial

    return (
      formIsDirty ||
      hasNewAnexos ||
      hasRemovedAnexos ||
      hasLinksChanged ||
      hasAutorizacoesChanged ||
      hasRequerAutorizacaoChanged ||
      hasAutorizacaoSequencialChanged ||
      hasGestorDemandanteChanged ||
      hasGestorDemandadoChanged ||
      hasExecutivoDemandanteChanged ||
      hasExecutivoDemandadoChanged ||
      hasUsaTemplateChanged ||
      hasSelectedTemplateIdChanged
    )
  }, [
    isEdit,
    form,
    anexos.length,
    anexosToRemove.length,
    linksIniciais,
    autorizacoesIniciais,
    ehSequencial,
    requerAutorizacaoPadrao,
    gestorDemandanteAtivo,
    gestorDemandadoAtivo,
    executivoDemandanteAtivo,
    executivoDemandadoAtivo,
    gestorDemandanteInicial,
    gestorDemandadoInicial,
    executivoDemandanteInicial,
    executivoDemandadoInicial,
    usaTemplate,
    usaTemplateInicial,
    selectedTemplateId,
    selectedTemplateIdInicial,
  ])

  const selectedSetor = useMemo(() => {
    if (!selectedSetorId) return null
    return setores.find(setor => setor.id.toString() === selectedSetorId) || null
  }, [selectedSetorId, setores])

  // Handlers para gerenciar autorizadores
  const handleAddAutorizadores = () => {
    if (selectedAutorizadorIds.length === 0) return

    const currentAutorizacoes = form.getValues('autorizacoes') || []
    const autorizacaoSequencial = form.getValues('autorizacao_sequencial')

    // Calcular próxima ordem se for sequencial
    const proximaOrdem = autorizacaoSequencial
      ? Math.max(0, ...currentAutorizacoes.map(a => a.ordem || 0)) + 1
      : 0

    // Converter IDs selecionados para autorizações (permite duplicação)
    const novasAutorizacoes = selectedAutorizadorIds
      .map(id => Number(id))
      .map((autorizadorId, index) => ({
        autorizador_fk: autorizadorId,
        ordem: autorizacaoSequencial ? proximaOrdem + index : null,
      }))

    // Adicionar à lista
    form.setValue('autorizacoes', [...currentAutorizacoes, ...novasAutorizacoes])

    // Resetar seleção
    setSelectedAutorizadorIds([])
  }

  const handleRemoveAutorizador = (index: number) => {
    const currentAutorizacoes = form.getValues('autorizacoes') || []
    const autorizacaoSequencial = form.getValues('autorizacao_sequencial')

    const novasAutorizacoes = currentAutorizacoes.filter((_, idx) => idx !== index)

    // Se for sequencial, recalcular ordens
    if (autorizacaoSequencial) {
      novasAutorizacoes.forEach((aut, idx) => {
        aut.ordem = idx + 1
      })
    }

    form.setValue('autorizacoes', novasAutorizacoes)
  }

  const handleMoveAutorizadorUp = (index: number) => {
    if (index === 0) return
    const currentAutorizacoes = [...(form.getValues('autorizacoes') || [])]
    ;[currentAutorizacoes[index - 1], currentAutorizacoes[index]] = [
      currentAutorizacoes[index],
      currentAutorizacoes[index - 1],
    ]

    // Atualizar ordens
    currentAutorizacoes.forEach((aut, idx) => {
      aut.ordem = idx + 1
    })

    form.setValue('autorizacoes', currentAutorizacoes)
  }

  const handleMoveAutorizadorDown = (index: number) => {
    const currentAutorizacoes = [...(form.getValues('autorizacoes') || [])]
    if (index === currentAutorizacoes.length - 1) return
    ;[currentAutorizacoes[index], currentAutorizacoes[index + 1]] = [
      currentAutorizacoes[index + 1],
      currentAutorizacoes[index],
    ]

    // Atualizar ordens
    currentAutorizacoes.forEach((aut, idx) => {
      aut.ordem = idx + 1
    })

    form.setValue('autorizacoes', currentAutorizacoes)
  }

  // Função para reordenar por drag and drop
  const handleReorderAutorizadores = (startIndex: number, endIndex: number) => {
    const currentAutorizacoes = [...(form.getValues('autorizacoes') || [])]
    const [movedItem] = currentAutorizacoes.splice(startIndex, 1)
    currentAutorizacoes.splice(endIndex, 0, movedItem)

    // Recalcular ordens sequenciais
    currentAutorizacoes.forEach((aut, idx) => {
      aut.ordem = idx + 1
    })

    form.setValue('autorizacoes', currentAutorizacoes)
  }

  // Encontrar autorizador por ID
  const getAutorizadorById = (id: number) => {
    return autorizadorOptions.find(aut => Number(aut.value) === id)
  }

  // Todos autorizadores disponíveis (permite duplicação)
  const availableAutorizadores = autorizadorOptions

  // Handlers para gerenciar links
  const handleAddLink = () => {
    const url = linkInput.trim()
    const title = linkTitleInput.trim()

    if (!url) return

    const currentLinks = form.getValues('links') || []

    // Normalizar URL - adicionar https:// se não tiver protocolo
    let normalizedUrl = url
    if (!normalizedUrl.match(/^https?:\/\//i)) {
      normalizedUrl = `https://${normalizedUrl}`
    }

    // Verificar se o link já foi adicionado
    if (currentLinks.find(l => l.link === normalizedUrl)) {
      return
    }

    // Validar URL
    try {
      new URL(normalizedUrl)
    } catch {
      return // URL inválida
    }

    // Se não tiver título, usar o domínio da URL como fallback
    const linkTitle = title || new URL(normalizedUrl).hostname

    // Adicionar à lista
    form.setValue('links', [...currentLinks, { link: normalizedUrl, titulo: linkTitle }])

    // Resetar inputs
    setLinkInput('')
    setLinkTitleInput('')
  }

  const handleRemoveLink = (linkToRemove: string) => {
    const currentLinks = form.getValues('links') || []
    form.setValue(
      'links',
      currentLinks.filter(l => l.link !== linkToRemove)
    )
  }

  // Loading state
  if (isLoading) {
    return (
      <div className='bg-white dark:bg-neutral-800 shadow-sm rounded-lg border border-gray-200 dark:border-neutral-700'>
        <div className='px-8 py-16 text-center'>
          <div className='inline-flex p-4 rounded-full bg-blue-100 dark:bg-blue-900/30 mb-6'>
            <svg
              className='animate-spin w-8 h-8 text-blue-600 dark:text-blue-400'
              fill='none'
              viewBox='0 0 24 24'
            >
              <circle
                className='opacity-25'
                cx='12'
                cy='12'
                r='10'
                stroke='currentColor'
                strokeWidth='4'
              ></circle>
              <path
                className='opacity-75'
                fill='currentColor'
                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
              ></path>
            </svg>
          </div>
          <h3 className='text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2'>
            Carregando seus setores...
          </h3>
          <p className='text-gray-600 dark:text-gray-400'>
            Verificando os setores que você tem permissão para criar processos
          </p>
        </div>
      </div>
    )
  }

  // No sectors available state
  if (!isLoading && setores.length === 0) {
    return (
      <div className='bg-white dark:bg-neutral-800 shadow-sm rounded-lg border border-gray-200 dark:border-neutral-700'>
        <div className='px-8 py-16 text-center'>
          <div className='inline-flex p-4 rounded-full bg-yellow-100 dark:bg-yellow-900/30 mb-6'>
            <svg
              className='w-8 h-8 text-yellow-600 dark:text-yellow-400'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z'
              />
            </svg>
          </div>
          <h3 className='text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3'>
            Sem permissão para criar processos
          </h3>
          <p className='text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto'>
            Você não está vinculado a nenhum setor que permita criar processos. Entre em contato com
            um administrador para obter as permissões necessárias.
          </p>
          <Button variant='outline' onClick={() => window.history.back()} className='px-6'>
            Voltar
          </Button>
        </div>
      </div>
    )
  }

  const handleFormSubmit = async (data: ProcessFormValues) => {
    // Mostrar modal de confirmação em vez de submeter diretamente
    setPendingData(data)
    setShowConfirmModal(true)
  }

  const handleConfirmSubmit = async () => {
    if (!pendingData) return

    try {
      setIsSubmitting(true)
      const data = pendingData

      // SEMPRE usar FormData quando houver anexos novos OU anexos a remover
      if (anexos.length > 0 || anexosToRemove.length > 0) {
        const formData = new FormData()

        // Adicionar todos os campos do formulário ao FormData
        formData.append('nome_processo', data.nome_processo)

        const tipoNormalizado = typeof data.tipo === 'string' ? data.tipo.trim() : ''
        if (tipoNormalizado) {
          formData.append('tipo', tipoNormalizado)
        }
        formData.append('prioridade_baixa', data.prioridade_baixa.toString())
        formData.append('prioridade_media', data.prioridade_media.toString())
        formData.append('prioridade_alta', data.prioridade_alta.toString())
        formData.append('prioridade_urgente', data.prioridade_urgente.toString())
        formData.append('setor_processo_fk', data.setor_processo_fk.toString())
        formData.append('descricao', data.descricao)

        // Adicionar instruções se existir
        if (data.instrucoes && data.instrucoes.trim()) {
          formData.append('instrucoes', data.instrucoes)
        }

        // Adicionar links - array de objetos ou flag para limpar
        if (data.links && data.links.length > 0) {
          data.links.forEach((linkObj: LinkProcesso, index: number) => {
            formData.append(`links[${index}][link]`, linkObj.link)
            if (linkObj.titulo) {
              formData.append(`links[${index}][titulo]`, linkObj.titulo)
            }
          })
        } else {
          // Flag para indicar que links devem ser limpos (FormData não suporta array vazio)
          formData.append('links_limpar', '1')
        }

        // Enviar autorizações se houver, senão enviar marcador de array vazio
        if (data.autorizacoes && data.autorizacoes.length > 0) {
          data.autorizacoes.forEach((aut: Autorizacao, index: number) => {
            formData.append(`autorizacoes[${index}][autorizador_fk]`, aut.autorizador_fk.toString())
            if (aut.ordem !== null && aut.ordem !== undefined) {
              formData.append(`autorizacoes[${index}][ordem]`, aut.ordem.toString())
            }
          })
        } else {
          // Para FormData, usar notação de array vazio que o Laravel entende
          // Não enviar nada para autorizacoes - o backend tratará como null/vazio
          // O campo 'autorizacoes_limpar' indica explicitamente para limpar autorizações existentes
          formData.append('autorizacoes_limpar', '1')
        }

        // Adicionar campos booleanos de autorização (gestores e executivos)
        formData.append('gestor_demandante', gestorDemandanteAtivo ? '1' : '0')
        formData.append('gestor_demandado', gestorDemandadoAtivo ? '1' : '0')
        formData.append('executivo_demandante', executivoDemandanteAtivo ? '1' : '0')
        formData.append('executivo_demandado', executivoDemandadoAtivo ? '1' : '0')

        // Adicionar campo equipe (pode ser aberto por equipe)
        formData.append('equipe', data.equipe ? '1' : '0')

        // Adicionar template_schema se um template foi selecionado
        if (usaTemplate && templateSchema) {
          appendNestedFormData(formData, templateSchema, 'template_schema')
        }

        // Adicionar anexos - mesmo padrão do createDemanda
        anexos.forEach(file => {
          formData.append('anexos[]', file)
        })

        // Adicionar lista de anexos a remover
        if (anexosToRemove.length > 0) {
          anexosToRemove.forEach((path, index) => {
            formData.append(`anexos_remover[${index}]`, path)
          })
        }

        await onSubmit(formData as unknown as CreateProcessoRequest)
      } else {
        // Sem anexos, enviar JSON normal
        const requestData: CreateProcessoRequest = {
          nome_processo: data.nome_processo,
          prioridade_baixa: data.prioridade_baixa,
          prioridade_media: data.prioridade_media,
          prioridade_alta: data.prioridade_alta,
          prioridade_urgente: data.prioridade_urgente,
          setor_processo_fk: Number(data.setor_processo_fk),
          descricao: data.descricao,
        }

        if (typeof data.tipo === 'string' && data.tipo.trim()) {
          requestData.tipo = data.tipo.trim()
        }

        // Adicionar instruções se fornecidas
        if (data.instrucoes && data.instrucoes.trim()) {
          requestData.instrucoes = data.instrucoes
        }

        // SEMPRE enviar links (mesmo vazio) para permitir limpar no backend
        requestData.links = data.links || []

        // SEMPRE enviar autorizações (mesmo vazio) para permitir limpar no backend
        requestData.autorizacoes = data.autorizacoes || []

        // Adicionar campos booleanos de autorização (enviar estado real)
        requestData.gestor_demandante = gestorDemandanteAtivo
        requestData.gestor_demandado = gestorDemandadoAtivo
        requestData.executivo_demandante = executivoDemandanteAtivo
        requestData.executivo_demandado = executivoDemandadoAtivo

        // Adicionar campo equipe (pode ser aberto por equipe)
        requestData.equipe = data.equipe

        // Adicionar template_schema se um template foi selecionado
        if (usaTemplate && templateSchema) {
          requestData.template_schema = templateSchema as unknown as Record<string, unknown>
        }

        await onSubmit(requestData)
      }

      // Resetar formulário após sucesso
      form.reset()
      setAnexos([])
      setAnexosToRemove([])
      setShowConfirmModal(false)
      setPendingData(null)

      // Redirecionar para a lista de processos
      navigate('/gestao/processos')
    } catch (error) {
      console.error('Erro ao criar processo:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancelConfirm = () => {
    setShowConfirmModal(false)
    setPendingData(null)
  }

  return (
    <>
      <div className='bg-white dark:bg-neutral-800 shadow-sm rounded-lg border border-gray-200 dark:border-neutral-700 overflow-hidden flex flex-col h-[calc(100vh-230px)]'>
        {/* Header */}
        <div className='px-6 py-6 border-b border-gray-200 dark:border-neutral-700 flex-shrink-0'>
          <h2 className='text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-3'>
            <RiFilePaper2Line className='text-blue-600 dark:text-blue-400' />
            {isEdit ? 'Editar Processo' : 'Criar Novo Processo'}
          </h2>
          <p className='mt-2 text-gray-600 dark:text-gray-400'>
            {isEdit
              ? 'Modifique as informações do processo conforme necessário.'
              : 'Defina um processo que será usado na criação de demandas.'}
          </p>
        </div>

        {/* Content */}
        <div className='px-6 py-6 overflow-y-auto flex-1'>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleFormSubmit)}
              className='space-y-6 max-w-6xl mx-auto'
            >
              {/* Grid Principal - Nome e Setor */}
              <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                {/* Nome do Processo */}
                <FormField
                  control={form.control}
                  name='nome_processo'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome do Processo</FormLabel>
                      <FormControl>
                        <div className='relative'>
                          {selectedSetorNome && (
                            <div className='absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-2 pointer-events-none z-10'>
                              <span className='text-sm font-medium  dark:bg-blue-900/30 px-2 '>
                                {selectedSetorNome}
                              </span>
                              <span className='text-black dark:text-gray-500'>-</span>
                            </div>
                          )}
                          <Input
                            placeholder={selectedSetorNome ? '' : 'Ex: Análise de Documentos'}
                            {...field}
                            maxLength={255}
                            className='text-base'
                            style={{
                              paddingLeft: selectedSetorNome ? `${selectedSetorNome.length * 8 + 40}px` : undefined
                            }}
                          />
                        </div>
                      </FormControl>
                      <FormDescription>
                        {selectedSetorNome 
                          ? 'O prefixo do setor será adicionado automaticamente pelo sistema'
                          : 'Selecione um setor para ver o prefixo que será adicionado'}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Tipo do Processo */}
                <FormField
                  control={form.control}
                  name='tipo'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo do Processo</FormLabel>
                      <FormControl>
                        <Input
                          placeholder='Ex: Documento de Suporte, Instrução de Trabalho...'
                          {...field}
                          maxLength={255}
                          className='text-base'
                        />
                      </FormControl>
                      <FormDescription>
                        Categoria ou tipo do processo (máx. 255 caracteres)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Grid Setor */}
              <div className='grid grid-cols-1 gap-6'>
                {/* Setor */}
                <FormField
                  control={form.control}
                  name='setor_processo_fk'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Setor Responsável</FormLabel>
                      <FormControl>
                        <SectorSelect
                          options={setorOptions}
                          value={field.value}
                          onChange={field.onChange}
                          placeholder='Selecione o setor responsável...'
                          disabled={isLoading}
                          searchable={true}
                        />
                      </FormControl>
                      <FormDescription>
                        {setorOptions.length === 0
                          ? 'Nenhum setor disponível'
                          : `Busque e selecione entre ${setorOptions.length} setor(es) disponível(is)`}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Prazos por Prioridade */}
              <div className='space-y-4'>
                <div className='flex items-center gap-2'>
                  <HiClock className='w-5 h-5 text-gray-600 dark:text-gray-400' />
                  <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100'>
                    Prazos por Prioridade
                  </h3>
                </div>
                <p className='text-sm text-gray-600 dark:text-gray-400'>
                  Defina quantos dias úteis são necessários para cada nível de prioridade
                </p>

                <div className='grid grid-cols-2 gap-4'>
                  {/* Prioridade Baixa */}
                  <FormField
                    control={form.control}
                    name='prioridade_baixa'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className='text-blue-700 font-medium flex items-center gap-2'>
                          <div className='w-2 h-2 bg-blue-500 rounded-full'></div>
                          Baixa
                        </FormLabel>
                        <FormControl>
                          <Input
                            type='number'
                            min='1'
                            max='365'
                            placeholder='8'
                            className='text-center'
                            value={field.value?.toString() || ''}
                            onChange={e => field.onChange(Number(e.target.value))}
                            onBlur={field.onBlur}
                            name={field.name}
                            ref={field.ref}
                          />
                        </FormControl>
                        <FormDescription className='text-xs text-center'>
                          dias úteis
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Prioridade Média */}
                  <FormField
                    control={form.control}
                    name='prioridade_media'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className='text-yellow-700 font-medium flex items-center gap-2'>
                          <div className='w-2 h-2 bg-yellow-500 rounded-full'></div>
                          Média
                        </FormLabel>
                        <FormControl>
                          <Input
                            type='number'
                            min='1'
                            max='365'
                            placeholder='6'
                            className='text-center'
                            value={field.value?.toString() || ''}
                            onChange={e => field.onChange(Number(e.target.value))}
                            onBlur={field.onBlur}
                            name={field.name}
                            ref={field.ref}
                          />
                        </FormControl>
                        <FormDescription className='text-xs text-center'>
                          dias úteis
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Prioridade Alta */}
                  <FormField
                    control={form.control}
                    name='prioridade_alta'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className='text-orange-700 font-medium flex items-center gap-2'>
                          <div className='w-2 h-2 bg-orange-500 rounded-full'></div>
                          Alta
                        </FormLabel>
                        <FormControl>
                          <Input
                            type='number'
                            min='1'
                            max='365'
                            placeholder='4'
                            className='text-center'
                            value={field.value?.toString() || ''}
                            onChange={e => field.onChange(Number(e.target.value))}
                            onBlur={field.onBlur}
                            name={field.name}
                            ref={field.ref}
                          />
                        </FormControl>
                        <FormDescription className='text-xs text-center'>
                          dias úteis
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Prioridade Urgente */}
                  <FormField
                    control={form.control}
                    name='prioridade_urgente'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className='text-red-700 font-medium flex items-center gap-2'>
                          <div className='w-2 h-2 bg-red-500 rounded-full'></div>
                          Urgente
                        </FormLabel>
                        <FormControl>
                          <Input
                            type='number'
                            min='1'
                            max='365'
                            placeholder='2'
                            className='text-center'
                            value={field.value?.toString() || ''}
                            onChange={e => field.onChange(Number(e.target.value))}
                            onBlur={field.onBlur}
                            name={field.name}
                            ref={field.ref}
                          />
                        </FormControl>
                        <FormDescription className='text-xs text-center'>
                          dias úteis
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Descrição */}
              <FormField
                control={form.control}
                name='descricao'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descrição do Processo</FormLabel>
                    <FormControl>
                      <Textarea
                        variant='rich'
                        placeholder='Descreva detalhadamente o processo, suas etapas e objetivos...'
                        value={field.value || ''}
                        onChange={field.onChange}
                        rows={4}
                        className='text-base'
                      />
                    </FormControl>
                    <FormDescription>
                      Descrição detalhada do processo, incluindo etapas e objetivos
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Instruções */}
              <FormField
                control={form.control}
                name='instrucoes'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='flex items-center gap-2'>
                      <FaBook className='w-4 h-4 text-blue-600 dark:text-blue-400' />
                      Instruções de Preenchimento
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        variant='rich'
                        placeholder='Digite as instruções de preenchimento usando as ferramentas de formatação...'
                        value={field.value || ''}
                        onChange={field.onChange}
                        rows={6}
                        className='text-base'
                        disabled={usaTemplate}
                      />
                    </FormControl>
                    <FormDescription>
                      {usaTemplate 
                        ? 'Desativado quando um template está selecionado. O template substitui as instruções.'
                        : 'Se seu processo tem um modelo pronto, como perguntas, formulários, etc, insira aqui para ajudar o usuário a preencher a demanda.'
                      }
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Sistema de Templates */}
              <div className='space-y-4 pt-4 border-t border-gray-200 dark:border-neutral-700'>
                <div className='flex items-center gap-2'>
                  <HiTemplate className='w-5 h-5 text-purple-600 dark:text-purple-400' />
                  <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100'>
                    Template de Formulário
                  </h3>
                  <span className='text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full'>
                    Opcional
                  </span>
                </div>
                <p className='text-sm text-gray-600 dark:text-gray-400'>
                  Utilize um template para criar formulários estruturados com campos específicos (selects, inputs, etc.)
                  ao invés de um texto livre nas instruções.
                </p>

                {/* Toggle: Usar Template */}
                <div className='flex flex-row items-center justify-between rounded-lg border p-4 bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800'>
                  <div className='space-y-0.5'>
                    <label className='text-sm font-medium text-gray-900 dark:text-gray-100'>
                      Usar Template de Formulário
                    </label>
                    <p className='text-xs text-gray-600 dark:text-gray-400'>
                      Quando ativado, o usuário preencherá um formulário estruturado ao criar a demanda
                    </p>
                  </div>
                  <button
                    type='button'
                    role='switch'
                    aria-checked={usaTemplate}
                    onClick={() => {
                      const novoValor = !usaTemplate
                      setUsaTemplate(novoValor)
                      if (novoValor) {
                        // Ao ativar template, limpar links, anexos e instruções
                        form.setValue('links', [])
                        form.setValue('instrucoes', '')
                        setLinkInput('')
                        setLinkTitleInput('')
                        setAnexos([])
                      } else {
                        setSelectedTemplateId(null)
                        setTemplateSchema(null)
                      }
                    }}
                    className={`
                      relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                      focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
                      ${usaTemplate ? 'bg-purple-600' : 'bg-gray-200 dark:bg-neutral-600'}
                    `}
                  >
                    <span
                      className={`
                        inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                        ${usaTemplate ? 'translate-x-6' : 'translate-x-1'}
                      `}
                    />
                  </button>
                </div>

                {/* Seletor de Template (quando ativado) */}
                <AnimatePresence>
                  {usaTemplate && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className='space-y-4'
                    >
                      <TemplateSelector
                        value={selectedTemplateId}
                        onChange={async (templateId) => {
                          setSelectedTemplateId(templateId)
                          if (templateId) {
                            setIsLoadingTemplate(true)
                            try {
                              const templateDef = getTemplateById(templateId)
                              if (templateDef) {
                                // Buscar unidades para campos dinâmicos
                                const unidades = setores
                                  .filter(s => !isEquipe(s))
                                  .map(s => ({ value: s.nome_setor, label: s.nome_setor }))
                                
                                const schema = await templateDef.getSchema({ unidades })
                                setTemplateSchema(schema)
                              }
                            } catch (error) {
                              console.error('Erro ao carregar template:', error)
                              setTemplateSchema(null)
                            } finally {
                              setIsLoadingTemplate(false)
                            }
                          } else {
                            setTemplateSchema(null)
                          }
                        }}
                        templates={templatesDisponiveis}
                        placeholder='Selecione um template...'
                        disabled={isLoadingTemplate}
                      />

                      {/* Preview do Template */}
                      {isLoadingTemplate ? (
                        <div className='flex items-center justify-center p-8'>
                          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600'></div>
                          <span className='ml-3 text-sm text-gray-600 dark:text-gray-400'>
                            Carregando template...
                          </span>
                        </div>
                      ) : templateSchema ? (
                        <TemplatePreview templateSchema={templateSchema} />
                      ) : null}

                      {/* Aviso sobre instruções */}
                      {usaTemplate && (
                        <div className='flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'>
                          <span className='text-amber-500'>⚠️</span>
                          <p className='text-xs text-amber-700 dark:text-amber-300'>
                            Ao usar um template, o campo &quot;Instruções de Preenchimento&quot; será ignorado.
                            O usuário verá o formulário do template ao criar a demanda.
                          </p>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Links */}
              <div className={`space-y-4 pt-4 border-t border-gray-200 dark:border-neutral-700 ${usaTemplate ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className='flex items-center gap-2'>
                  <RiLinksLine className='w-5 h-5 text-gray-600 dark:text-gray-400' />
                  <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100'>
                    Links Úteis
                  </h3>
                  {usaTemplate && (
                    <span className='text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full'>
                      Desabilitado com template
                    </span>
                  )}
                </div>
                <p className='text-sm text-gray-600 dark:text-gray-400'>
                  {usaTemplate 
                    ? 'Links não podem ser adicionados quando um template está ativo'
                    : 'Adicione links de referência, manuais ou documentos relacionados ao processo'}
                </p>

                {/* Input para adicionar link */}
                <div className='rounded-lg border p-4 bg-blue-50 dark:bg-blue-900/20'>
                  <h4 className='font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2'>
                    <RiLinksLine className='w-4 h-4 text-blue-600 dark:text-blue-400' />
                    Adicionar Link
                  </h4>
                  <div className='space-y-3'>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                        Título do Link (opcional)
                      </label>
                      <Input
                        type='text'
                        value={linkTitleInput}
                        onChange={e => setLinkTitleInput(e.target.value)}
                        placeholder='Ex: Manual RCA SESI, Portal Indústria, etc.'
                        className='w-full'
                      />
                      <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>
                        Nome amigável para identificar o link (se vazio, usará o domínio)
                      </p>
                    </div>
                    <div className='flex gap-2'>
                      <div className='flex-1'>
                        <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                          URL *
                        </label>
                        <Input
                          type='text'
                          value={linkInput}
                          onChange={e => setLinkInput(e.target.value)}
                          onKeyPress={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              handleAddLink()
                            }
                          }}
                          placeholder='exemplo.com/manual.pdf'
                          className='w-full'
                        />
                      </div>
                      <div className='flex items-end'>
                        <Button
                          type='button'
                          onClick={handleAddLink}
                          disabled={!linkInput.trim()}
                          size='sm'
                          className='mb-0.5'
                        >
                          <RiAddLine className='w-4 h-4 mr-1' />
                          Adicionar
                        </Button>
                      </div>
                    </div>
                    <p className='text-xs text-gray-600 dark:text-gray-400'>
                      💡 Dica: Adicione um título descritivo para facilitar a identificação
                    </p>
                  </div>
                </div>

                {/* Lista de Links Adicionados */}
                {linksSelecionados.length > 0 && (
                  <div className='rounded-lg border p-4'>
                    <h4 className='font-medium text-gray-900 dark:text-gray-100 mb-3'>
                      Links Adicionados ({linksSelecionados.length})
                    </h4>
                    <div className='space-y-2'>
                      {linksSelecionados.map((linkObj, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          className='flex items-center justify-between p-3 bg-gray-50 dark:bg-neutral-700 rounded-lg'
                        >
                          <div className='flex items-center gap-3 flex-1 min-w-0'>
                            <div className='w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0'>
                              <RiLinksLine className='w-4 h-4 text-blue-600 dark:text-blue-400' />
                            </div>
                            <div className='min-w-0 flex-1'>
                              {/* Título do link (destaque) */}
                              <div className='flex items-center gap-2'>
                                <a
                                  href={linkObj.link}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  className='text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline'
                                  title={`Acessar: ${linkObj.link}`}
                                >
                                  {linkObj.titulo || new URL(linkObj.link).hostname}
                                </a>
                                <span className='text-xs text-gray-400 dark:text-gray-500'>↗</span>
                              </div>
                              {/* URL completa (secundário, menor) */}
                              <p
                                className='text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5'
                                title={linkObj.link}
                              >
                                {linkObj.link}
                              </p>
                            </div>
                          </div>
                          <Button
                            type='button'
                            variant='ghost'
                            size='sm'
                            onClick={() => handleRemoveLink(linkObj.link)}
                            className='text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0'
                          >
                            <RiDeleteBin6Line className='w-4 h-4' />
                          </Button>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Mensagem de validação para links */}
                <FormField
                  control={form.control}
                  name='links'
                  render={() => (
                    <FormItem>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Seção de Anexos */}
              <div className={`space-y-4 pt-4 border-t border-gray-200 dark:border-neutral-700 ${usaTemplate ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className='flex items-center gap-2'>
                  <RiAttachmentLine className='w-5 h-5 text-gray-600 dark:text-gray-400' />
                  <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100'>Anexos</h3>
                  {usaTemplate && (
                    <span className='text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full'>
                      Desabilitado com template
                    </span>
                  )}
                </div>
                <p className='text-sm text-gray-600 dark:text-gray-400'>
                  {usaTemplate
                    ? 'Anexos não podem ser adicionados quando um template está ativo'
                    : 'Adicione múltiplos arquivos de referência, templates ou documentos relacionados ao processo'}
                </p>

                {/* Input para adicionar anexos */}
                <div className='rounded-lg border p-4 bg-purple-50 dark:bg-purple-900/20'>
                  <h4 className='font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2'>
                    <RiAttachmentLine className='w-4 h-4 text-purple-600 dark:text-purple-400' />
                    Adicionar Anexos
                  </h4>
                  <div className='flex gap-2'>
                    <Input
                      id='file-input-anexos'
                      type='file'
                      multiple
                      onChange={e => {
                        const files = Array.from(e.target.files || [])
                        if (files.length > 0) {
                          setAnexos(prev => [...prev, ...files])
                          // Reset do input para permitir adicionar o mesmo arquivo novamente se necessário
                          e.target.value = ''
                        }
                      }}
                      accept='.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.zip'
                      className='cursor-pointer'
                    />
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={() => document.getElementById('file-input-anexos')?.click()}
                      className='whitespace-nowrap'
                    >
                      <RiAddLine className='w-4 h-4 mr-1' />
                      Selecionar
                    </Button>
                  </div>
                  <p className='text-xs text-gray-600 dark:text-gray-400 mt-2'>
                    Formatos aceitos: PDF, Word, Excel, Imagens, ZIP • Máx. 10MB por arquivo •
                    Selecione múltiplos arquivos de uma vez ou adicione aos poucos
                  </p>
                </div>

                {/* Anexos Existentes (ao editar) */}
                {isEdit &&
                  initialData?.anexos &&
                  Array.isArray(initialData.anexos) &&
                  initialData.anexos.length > 0 && (
                    <div className='rounded-lg border p-4 bg-blue-50 dark:bg-blue-900/20'>
                      <h4 className='font-medium text-gray-900 dark:text-gray-100 mb-3'>
                        Anexos Existentes (
                        {
                          initialData.anexos.filter(
                            (a: AnexoProcesso) => !anexosToRemove.includes(a.anexo)
                          ).length
                        }
                        )
                      </h4>
                      <div className='space-y-2'>
                        {initialData.anexos
                          .filter((a: AnexoProcesso) => !anexosToRemove.includes(a.anexo))
                          .map((anexoObj: AnexoProcesso, index: number) => {
                            return (
                              <div
                                key={index}
                                className='flex items-center justify-between p-3 bg-white dark:bg-neutral-700 rounded-lg border border-blue-200 dark:border-blue-800 group'
                              >
                                <button
                                  type='button'
                                  onClick={() => handleDownloadAttachment(anexoObj)}
                                  className='flex items-center text-start gap-3 flex-1 min-w-0 hover:bg-blue-50 dark:hover:bg-neutral-600 transition-colors rounded-l-lg -m-3 p-3 mr-2'
                                >
                                  <div className='w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0'>
                                    <RiAttachmentLine className='w-4 h-4 text-blue-600 dark:text-blue-400' />
                                  </div>
                                  <div className='min-w-0 flex-1'>
                                    <p className='text-sm font-medium text-gray-900 dark:text-gray-100 truncate'>
                                      {anexoObj.nome_anexo || `Anexo ${index + 1}`}
                                    </p>
                                    <p className='text-xs text-gray-500 dark:text-gray-400'>
                                      Clique para baixar
                                    </p>
                                  </div>
                                  <RiDownloadLine className='w-4 h-4 text-blue-600 dark:text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0' />
                                </button>
                                <Button
                                  type='button'
                                  variant='ghost'
                                  size='sm'
                                  onClick={() =>
                                    setAnexosToRemove(prev => [...prev, anexoObj.anexo])
                                  }
                                  className='text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 flex-shrink-0'
                                  title='Remover anexo'
                                >
                                  <RiDeleteBin6Line className='w-4 h-4' />
                                </Button>
                              </div>
                            )
                          })}
                      </div>
                      {anexosToRemove.length > 0 ? (
                        <p className='text-xs text-red-600 dark:text-red-400 mt-3 flex items-center gap-1'>
                          <span>⚠️</span>
                          <span>
                            {anexosToRemove.length} anexo(s) será(ão) removido(s) ao salvar
                          </span>
                        </p>
                      ) : (
                        <p className='text-xs text-gray-600 dark:text-gray-400 mt-2'>
                          💡 Novos anexos serão adicionados aos existentes
                        </p>
                      )}
                    </div>
                  )}

                {/* Lista de Anexos Adicionados */}
                {anexos.length > 0 && (
                  <div className='rounded-lg border p-4 border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-900/10'>
                    <div className='flex items-center justify-between mb-3'>
                      <h4 className='font-medium text-gray-900 dark:text-gray-100'>
                        Novos Anexos ({anexos.length})
                      </h4>
                      <div className='text-xs text-gray-600 dark:text-gray-400'>
                        Total:{' '}
                        {(anexos.reduce((acc, file) => acc + file.size, 0) / (1024 * 1024)).toFixed(
                          2
                        )}{' '}
                        MB
                      </div>
                    </div>
                    <div className='space-y-2 max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-purple-300 dark:scrollbar-thumb-purple-600 scrollbar-track-transparent'>
                      {anexos.map((file, index) => {
                        const fileSizeMB = file.size / (1024 * 1024)
                        const isLarge = fileSizeMB > 10

                        return (
                          <motion.div
                            key={`${file.name}-${index}`}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            className={`flex items-center justify-between p-3 rounded-lg ${
                              isLarge
                                ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                                : 'bg-white dark:bg-neutral-700 border border-purple-200 dark:border-purple-800'
                            }`}
                          >
                            <div className='flex items-center gap-3 flex-1 min-w-0'>
                              <div
                                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  isLarge
                                    ? 'bg-red-100 dark:bg-red-900/30'
                                    : 'bg-purple-100 dark:bg-purple-900/30'
                                }`}
                              >
                                <RiAttachmentLine
                                  className={`w-4 h-4 ${
                                    isLarge
                                      ? 'text-red-600 dark:text-red-400'
                                      : 'text-purple-600 dark:text-purple-400'
                                  }`}
                                />
                              </div>
                              <div className='min-w-0 flex-1'>
                                <p
                                  className='text-sm font-medium text-gray-900 dark:text-gray-100 truncate'
                                  title={file.name}
                                >
                                  {file.name}
                                </p>
                                <div className='flex items-center gap-2'>
                                  <p className='text-xs text-gray-500 dark:text-gray-400'>
                                    {fileSizeMB >= 1
                                      ? `${fileSizeMB.toFixed(2)} MB`
                                      : `${(file.size / 1024).toFixed(2)} KB`}
                                  </p>
                                  {isLarge && (
                                    <span className='text-xs text-red-600 dark:text-red-400 font-medium'>
                                      ⚠️ Arquivo muito grande
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <Button
                              type='button'
                              variant='ghost'
                              size='sm'
                              onClick={() => setAnexos(prev => prev.filter((_, i) => i !== index))}
                              className='text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 flex-shrink-0'
                              title='Remover anexo'
                            >
                              <RiDeleteBin6Line className='w-4 h-4' />
                            </Button>
                          </motion.div>
                        )
                      })}
                    </div>
                    {anexos.some(f => f.size > 10 * 1024 * 1024) && (
                      <p className='text-xs text-red-600 dark:text-red-400 mt-2 flex items-center gap-1'>
                        <span>⚠️</span>
                        Alguns arquivos excedem 10MB e podem falhar no upload
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Seção de Autorização */}
              <div className='space-y-4 pt-4 border-t border-gray-200 dark:border-neutral-700'>
                <div className='flex items-center gap-2'>
                  <FaLock className='w-5 h-5 text-gray-600 dark:text-gray-400' />
                  <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100'>
                    Configuração de Autorização
                  </h3>
                </div>

                {/* Toggle: Pode ser aberto por equipe */}
                <FormField
                  control={form.control}
                  name='equipe'
                  render={({ field }) => (
                    <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4 bg-gray-50 dark:bg-neutral-700'>
                      <div className='space-y-0.5'>
                        <FormLabel className='text-base font-medium'>
                          Pode ser aberto por uma equipe?
                        </FormLabel>
                        <FormDescription>
                          Ative se este processo pode ser aberto por uma equipe
                        </FormDescription>
                      </div>
                      <FormControl>
                        <button
                          type='button'
                          role='switch'
                          aria-checked={field.value}
                          onClick={() => field.onChange(!field.value)}
                          className={`
                              relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                              ${field.value ? 'bg-blue-600' : 'bg-gray-300 dark:bg-neutral-600'}
                            `}
                        >
                          <span
                            className={`
                                inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                                ${field.value ? 'translate-x-6' : 'translate-x-1'}
                              `}
                          />
                        </button>
                      </FormControl>
                    </FormItem>
                  )}
                />

                {/* Toggle: Requer Autorização */}
                <FormField
                  control={form.control}
                  name='requer_autorizacao'
                  render={({ field }) => (
                    <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4 bg-gray-50 dark:bg-neutral-700'>
                      <div className='space-y-0.5'>
                        <FormLabel className='text-base font-medium'>Requer Autorização?</FormLabel>
                        <FormDescription>
                          Ative se este processo precisa de aprovação antes de finalizar
                        </FormDescription>
                      </div>
                      <FormControl>
                        <button
                          type='button'
                          role='switch'
                          aria-checked={field.value}
                          onClick={() => {
                            const nextValue = !field.value
                            field.onChange(nextValue)

                            // Se desativar autorização, limpar todos os toggles de gestor/executivo
                            if (!nextValue) {
                              if (gestorDemandanteAtivo) {
                                setGestorDemandanteAtivo(false)
                                form.setValue('gestor_demandante', false, { shouldDirty: true })
                              }
                              if (gestorDemandadoAtivo) {
                                setGestorDemandadoAtivo(false)
                                form.setValue('gestor_demandado', false, { shouldDirty: true })
                              }
                              if (executivoDemandanteAtivo) {
                                setExecutivoDemandanteAtivo(false)
                                form.setValue('executivo_demandante', false, { shouldDirty: true })
                              }
                              if (executivoDemandadoAtivo) {
                                setExecutivoDemandadoAtivo(false)
                                form.setValue('executivo_demandado', false, { shouldDirty: true })
                              }
                            }
                          }}
                          className={`
                              relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                              ${field.value ? 'bg-blue-600' : 'bg-gray-300 dark:bg-neutral-600'}
                            `}
                        >
                          <span
                            className={`
                                inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                                ${field.value ? 'translate-x-6' : 'translate-x-1'}
                              `}
                          />
                        </button>
                      </FormControl>
                    </FormItem>
                  )}
                />

                {/* Mostrar configurações de autorização se habilitado */}
                <AnimatePresence>
                  {requerAutorizacao && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className='space-y-4'
                    >
                      {/* Toggle: Autorização Sequencial */}
                      <FormField
                        control={form.control}
                        name='autorizacao_sequencial'
                        render={({ field }) => (
                          <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
                            <div className='space-y-0.5'>
                              <FormLabel className='text-base font-medium'>
                                Autorização Sequencial?
                              </FormLabel>
                              <FormDescription>
                                Defina se as autorizações devem seguir uma ordem específica
                              </FormDescription>
                            </div>
                            <FormControl>
                              <button
                                type='button'
                                role='switch'
                                aria-checked={field.value}
                                onClick={() => {
                                  const nextSequential = !field.value
                                  field.onChange(nextSequential)

                                  if (!nextSequential) {
                                    const currentAutorizacoes = form.getValues('autorizacoes') || []
                                    form.setValue(
                                      'autorizacoes',
                                      currentAutorizacoes.map(a => ({ ...a, ordem: null }))
                                    )

                                    if (gestorDemandanteAtivo) {
                                      setGestorDemandanteAtivo(false)
                                      form.setValue('gestor_demandante', false, {
                                        shouldDirty: true,
                                      })
                                    }
                                    if (gestorDemandadoAtivo) {
                                      setGestorDemandadoAtivo(false)
                                      form.setValue('gestor_demandado', false, {
                                        shouldDirty: true,
                                      })
                                    }
                                    if (executivoDemandanteAtivo) {
                                      setExecutivoDemandanteAtivo(false)
                                      form.setValue('executivo_demandante', false, {
                                        shouldDirty: true,
                                      })
                                    }
                                    if (executivoDemandadoAtivo) {
                                      setExecutivoDemandadoAtivo(false)
                                      form.setValue('executivo_demandado', false, {
                                        shouldDirty: true,
                                      })
                                    }
                                  } else {
                                    // Se habilitar sequencial, definir ordens automaticamente na ordem atual
                                    const currentAutorizacoes = form.getValues('autorizacoes') || []
                                    if (currentAutorizacoes.length > 0) {
                                      form.setValue(
                                        'autorizacoes',
                                        currentAutorizacoes.map((a, index) => ({
                                          ...a,
                                          ordem: index + 1,
                                        }))
                                      )
                                    }
                                  }
                                }}
                                className={`
                                    relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                                    ${field.value ? 'bg-blue-600' : 'bg-gray-300 dark:bg-neutral-600'}
                                  `}
                              >
                                <span
                                  className={`
                                      inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                                      ${field.value ? 'translate-x-6' : 'translate-x-1'}
                                    `}
                                />
                              </button>
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      {/* Switches de Gestores e Executivos - Aparecem apenas com autorização sequencial */}
                      {autorizacaoSequencial && (
                        <div className='space-y-4'>
                          {/* Grid de Gestores */}
                          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                            {/* Toggle: Gestor da Unidade Demandante - Default */}
                            <div className='flex flex-row items-center justify-between rounded-lg border p-4 bg-gray-50 dark:bg-neutral-700'>
                              <div className='space-y-0.5'>
                                <div className='flex items-center gap-2'>
                                  <span className='text-base font-medium dark:text-gray-300'>
                                    Gestor da Unidade Demandante
                                  </span>
                                </div>
                                <p className='text-sm text-gray-700 dark:text-gray-400'>
                                  Aprovação do gestor da unidade demandante
                                </p>
                              </div>
                              <button
                                type='button'
                                role='switch'
                                aria-checked={gestorDemandanteAtivo}
                                onClick={() => {
                                  const next = !gestorDemandanteAtivo
                                  setGestorDemandanteAtivo(next)
                                  form.setValue('gestor_demandante', next, { shouldDirty: true })
                                }}
                                className={`
                            relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                            ${gestorDemandanteAtivo ? 'bg-blue-600' : 'bg-gray-300 dark:bg-neutral-600'}
                          `}
                              >
                                <span
                                  className={`
                              inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                              ${gestorDemandanteAtivo ? 'translate-x-6' : 'translate-x-1'}
                            `}
                                />
                              </button>
                            </div>

                            {/* Toggle: Gestor da Unidade Demandada - Default */}
                            <div className='flex flex-row items-center justify-between rounded-lg border p-4 bg-gray-50 dark:bg-neutral-700'>
                              <div className='space-y-0.5'>
                                <div className='flex items-center gap-2'>
                                  <span className='text-base font-medium dark:text-gray-300'>
                                    Gestor da Unidade Demandada
                                  </span>
                                </div>
                                <p className='text-sm text-gray-700 dark:text-gray-400'>
                                  Aprovação do gestor da unidade demandada
                                </p>
                              </div>
                              <button
                                type='button'
                                role='switch'
                                aria-checked={gestorDemandadoAtivo}
                                onClick={() => {
                                  const next = !gestorDemandadoAtivo
                                  setGestorDemandadoAtivo(next)
                                  form.setValue('gestor_demandado', next, { shouldDirty: true })
                                }}
                                className={`
                            relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                            ${gestorDemandadoAtivo ? 'bg-blue-600' : 'bg-gray-300 dark:bg-neutral-600'}
                          `}
                              >
                                <span
                                  className={`
                              inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                              ${gestorDemandadoAtivo ? 'translate-x-6' : 'translate-x-1'}
                            `}
                                />
                              </button>
                            </div>
                          </div>

                          {/* Grid de Executivos */}
                          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                            {/* Toggle: Executivo da Unidade Demandante - Default */}
                            <div className='flex flex-row items-center justify-between rounded-lg border p-4 bg-gray-50 dark:bg-neutral-700'>
                              <div className='space-y-0.5'>
                                <div className='flex items-center gap-2'>
                                  <span className='text-base font-medium dark:text-gray-300'>
                                    Executivo da Unidade Demandante
                                  </span>
                                </div>
                                <p className='text-sm text-gray-700 dark:text-gray-400'>
                                  Aprovação do executivo da unidade demandante
                                </p>
                              </div>
                              <button
                                type='button'
                                role='switch'
                                aria-checked={executivoDemandanteAtivo}
                                onClick={() => {
                                  const next = !executivoDemandanteAtivo
                                  setExecutivoDemandanteAtivo(next)
                                  form.setValue('executivo_demandante', next, { shouldDirty: true })
                                }}
                                className={`
                            relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                            ${executivoDemandanteAtivo ? 'bg-blue-600' : 'bg-gray-300 dark:bg-neutral-600'}
                          `}
                              >
                                <span
                                  className={`
                              inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                              ${executivoDemandanteAtivo ? 'translate-x-6' : 'translate-x-1'}
                            `}
                                />
                              </button>
                            </div>

                            {/* Toggle: Executivo da Unidade Demandada - Default */}
                            <div className='flex flex-row items-center justify-between rounded-lg border p-4 bg-gray-50 dark:bg-neutral-700'>
                              <div className='space-y-0.5'>
                                <div className='flex items-center gap-2'>
                                  <span className='text-base font-medium dark:text-gray-300'>
                                    Executivo da Unidade Demandada
                                  </span>
                                </div>
                                <p className='text-sm text-gray-700 dark:text-gray-400'>
                                  Aprovação do executivo da unidade demandada
                                </p>
                              </div>
                              <button
                                type='button'
                                role='switch'
                                aria-checked={executivoDemandadoAtivo}
                                onClick={() => {
                                  const next = !executivoDemandadoAtivo
                                  setExecutivoDemandadoAtivo(next)
                                  form.setValue('executivo_demandado', next, { shouldDirty: true })
                                }}
                                className={`
                            relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                            ${executivoDemandadoAtivo ? 'bg-blue-600' : 'bg-gray-300 dark:bg-neutral-600'}
                          `}
                              >
                                <span
                                  className={`
                              inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                              ${executivoDemandadoAtivo ? 'translate-x-6' : 'translate-x-1'}
                            `}
                                />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Adicionar Autorizador */}
                      <div className='rounded-lg border p-4 bg-blue-50 dark:bg-blue-900/20'>
                        <h4 className='font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2'>
                          <FaUserShield className='w-4 h-4 text-blue-600 dark:text-blue-400' />
                          Selecionar Autorizadores
                        </h4>

                        {isLoadingSetores ? (
                          <div className='text-center py-4'>
                            <p className='text-sm text-gray-600 dark:text-gray-400'>
                              Carregando autorizadores...
                            </p>
                          </div>
                        ) : availableAutorizadores.length === 0 ? (
                          <div className='text-center py-4'>
                            <p className='text-sm text-gray-600 dark:text-gray-400'>
                              Nenhum setor ou equipe cadastrado no sistema
                            </p>
                          </div>
                        ) : (
                          <div className='space-y-3'>
                            <div className='flex gap-2 items-end'>
                              <div className='flex-1'>
                                <AuthorizerMultiSelect
                                  options={availableAutorizadores}
                                  value={selectedAutorizadorIds}
                                  onChange={setSelectedAutorizadorIds}
                                  placeholder='Selecione um ou mais autorizadores...'
                                />
                              </div>

                              <Button
                                type='button'
                                onClick={handleAddAutorizadores}
                                disabled={selectedAutorizadorIds.length === 0}
                                size='sm'
                                className='whitespace-nowrap'
                              >
                                <RiAddLine className='w-4 h-4 mr-1' />
                                Adicionar{' '}
                                {selectedAutorizadorIds.length > 0
                                  ? `(${selectedAutorizadorIds.length})`
                                  : ''}
                              </Button>
                            </div>

                            <div className='text-center'>
                              <p className='text-xs text-gray-600 dark:text-gray-400'>
                                {selectedAutorizadorIds.length > 0
                                  ? `${selectedAutorizadorIds.length} autorizador${selectedAutorizadorIds.length !== 1 ? 'es' : ''} selecionado${selectedAutorizadorIds.length !== 1 ? 's' : ''}`
                                  : 'Selecione autorizadores para adicionar'}
                                {autorizacaoSequencial && selectedAutorizadorIds.length > 0 && (
                                  <span className='block mt-1'>
                                    A ordem será definida automaticamente na sequência
                                  </span>
                                )}
                              </p>
                              <p className='text-xs text-blue-600 dark:text-blue-400 mt-2'>
                                💡 Você pode adicionar o mesmo setor/equipe múltiplas vezes
                              </p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Lista de Autorizadores Selecionados */}
                      {(autorizacoesSelecionadas.length > 0 ||
                        gestorDemandanteAtivo ||
                        executivoDemandanteAtivo ||
                        gestorDemandadoAtivo ||
                        executivoDemandadoAtivo) && (
                        <div className='rounded-lg border p-4'>
                          <h4 className='font-medium text-gray-900 dark:text-gray-100 mb-3'>
                            Ordem de Autorização (
                            {(() => {
                              let total = autorizacoesSelecionadas.length
                              if (gestorDemandanteAtivo) total++
                              if (executivoDemandanteAtivo) total++
                              if (gestorDemandadoAtivo) total++
                              if (executivoDemandadoAtivo) total++
                              return total
                            })()}
                            )
                            {autorizacaoSequencial && autorizacoesSelecionadas.length > 0 && (
                              <span className='text-xs text-gray-500 dark:text-gray-400 ml-2'>
                                • Arraste e solte os autorizadores para reordenar
                              </span>
                            )}
                          </h4>
                          <div className='space-y-2'>
                            {/* Gestores/Executivos da Unidade Demandante (início) */}
                            {gestorDemandanteAtivo && (
                              <div className='flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-2 border-blue-300 dark:border-blue-700'>
                                <div className='w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0 font-bold text-sm'>
                                  1
                                </div>
                                <div className='flex-1 min-w-0'>
                                  <p className='text-sm font-semibold text-blue-900 dark:text-blue-100'>
                                    Gestor da Unidade Demandante
                                  </p>
                                  <p className='text-xs text-blue-700 dark:text-blue-300'>
                                    Prioridade fixa • Sempre primeiro
                                  </p>
                                </div>
                                <div className='flex items-center gap-2 flex-shrink-0'>
                                  <span className='px-2 py-1 bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-200 text-xs font-medium rounded'>
                                    Gestor
                                  </span>
                                  <FaUserShield className='w-5 h-5 text-blue-600 dark:text-blue-400' />
                                </div>
                              </div>
                            )}

                            {executivoDemandanteAtivo && (
                              <div className='flex items-center gap-3 p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border-2 border-indigo-300 dark:border-indigo-700'>
                                <div className='w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center flex-shrink-0 font-bold text-sm'>
                                  {gestorDemandanteAtivo ? '2' : '1'}
                                </div>
                                <div className='flex-1 min-w-0'>
                                  <p className='text-sm font-semibold text-indigo-900 dark:text-indigo-100'>
                                    Executivo da Unidade Demandante
                                  </p>
                                  <p className='text-xs text-indigo-700 dark:text-indigo-300'>
                                    Prioridade fixa •{' '}
                                    {gestorDemandanteAtivo
                                      ? 'Segundo (após Gestor Demandante)'
                                      : 'Sempre primeiro'}
                                  </p>
                                </div>
                                <div className='flex items-center gap-2 flex-shrink-0'>
                                  <span className='px-2 py-1 bg-indigo-100 dark:bg-indigo-800 text-indigo-700 dark:text-indigo-200 text-xs font-medium rounded'>
                                    Executivo
                                  </span>
                                  <FaUserShield className='w-5 h-5 text-indigo-600 dark:text-indigo-400' />
                                </div>
                              </div>
                            )}

                            {/* Autorizadores Customizados (meio) */}
                            {autorizacoesSelecionadas
                              .sort((a, b) => {
                                if (!autorizacaoSequencial) return 0
                                return (a.ordem || 0) - (b.ordem || 0)
                              })
                              .map((aut, index) => {
                                const autorizador = getAutorizadorById(aut.autorizador_fk)
                                if (!autorizador) return null

                                // Calcular ordem visual real
                                let ordemVisual = index + 1
                                if (gestorDemandanteAtivo) ordemVisual++
                                if (executivoDemandanteAtivo) ordemVisual++

                                return (
                                  <DraggableAutorizerItem
                                    key={`${aut.autorizador_fk}-${index}`}
                                    aut={aut}
                                    index={index}
                                    autorizador={autorizador}
                                    isSequencial={!!autorizacaoSequencial}
                                    onMoveUp={handleMoveAutorizadorUp}
                                    onMoveDown={handleMoveAutorizadorDown}
                                    onRemove={handleRemoveAutorizador}
                                    onReorder={handleReorderAutorizadores}
                                    totalItems={autorizacoesSelecionadas.length}
                                    ordemVisual={ordemVisual}
                                  />
                                )
                              })}

                            {/* Gestores/Executivos da Unidade Demandada (fim) */}
                            {/* Executivo aparece antes do Gestor quando ambos estão ativos */}
                            {executivoDemandadoAtivo && (
                              <div className='flex items-center gap-3 p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg border-2 border-teal-300 dark:border-teal-700'>
                                <div className='w-8 h-8 rounded-full bg-teal-600 text-white flex items-center justify-center flex-shrink-0 font-bold text-sm'>
                                  {(() => {
                                    let ordem = 1
                                    if (gestorDemandanteAtivo) ordem++
                                    if (executivoDemandanteAtivo) ordem++
                                    ordem += autorizacoesSelecionadas.length
                                    return ordem
                                  })()}
                                </div>
                                <div className='flex-1 min-w-0'>
                                  <p className='text-sm font-semibold text-teal-900 dark:text-teal-100'>
                                    Executivo da Unidade Demandada
                                  </p>
                                  <p className='text-xs text-teal-700 dark:text-teal-300'>
                                    Prioridade fixa •{' '}
                                    {gestorDemandadoAtivo
                                      ? 'Penúltimo (antes do Gestor Demandado)'
                                      : 'Sempre último'}
                                  </p>
                                </div>
                                <div className='flex items-center gap-2 flex-shrink-0'>
                                  <span className='px-2 py-1 bg-teal-100 dark:bg-teal-800 text-teal-700 dark:text-teal-200 text-xs font-medium rounded'>
                                    Executivo
                                  </span>
                                  <FaUserShield className='w-5 h-5 text-teal-600 dark:text-teal-400' />
                                </div>
                              </div>
                            )}

                            {gestorDemandadoAtivo && (
                              <div className='flex items-center gap-3 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border-2 border-emerald-300 dark:border-emerald-700'>
                                <div className='w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center flex-shrink-0 font-bold text-sm'>
                                  {(() => {
                                    let ordem = 1
                                    if (gestorDemandanteAtivo) ordem++
                                    if (executivoDemandanteAtivo) ordem++
                                    ordem += autorizacoesSelecionadas.length
                                    if (executivoDemandadoAtivo) ordem++
                                    return ordem
                                  })()}
                                </div>
                                <div className='flex-1 min-w-0'>
                                  <p className='text-sm font-semibold text-emerald-900 dark:text-emerald-100'>
                                    Gestor da Unidade Demandada
                                  </p>
                                  <p className='text-xs text-emerald-700 dark:text-emerald-300'>
                                    Prioridade fixa • Sempre último
                                  </p>
                                </div>
                                <div className='flex items-center gap-2 flex-shrink-0'>
                                  <span className='px-2 py-1 bg-emerald-100 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-200 text-xs font-medium rounded'>
                                    Gestor
                                  </span>
                                  <FaUserShield className='w-5 h-5 text-emerald-600 dark:text-emerald-400' />
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Legenda explicativa */}
                          {(gestorDemandanteAtivo ||
                            executivoDemandanteAtivo ||
                            gestorDemandadoAtivo ||
                            executivoDemandadoAtivo) && (
                            <div className='mt-4 p-3 bg-gray-50 dark:bg-neutral-700 rounded-lg border border-gray-200 dark:border-neutral-600'>
                              <p className='text-xs text-gray-600 dark:text-gray-400'>
                                <span className='font-semibold'>💡 Ordem de Prioridade:</span>{' '}
                                Gestores/Executivos têm posições fixas (início ou fim). Apenas os
                                autorizadores customizados podem ser reordenados entre eles.
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Mensagem de validação */}
                      <FormField
                        control={form.control}
                        name='autorizacoes'
                        render={() => (
                          <FormItem>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </form>
          </Form>
        </div>

        {/* Footer com botões - Fixo */}
        <div className='px-6 py-4 border-t border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 flex-shrink-0'>
          <div className='flex flex-col gap-3 max-w-5xl mx-auto'>
            {/* Mensagem quando não há alterações */}
            {/* {isEdit && !hasFormChanges && (
              <div className="text-center">
                <p className="text-sm text-amber-600 dark:text-amber-400 flex items-center justify-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Faça alguma alteração no formulário para salvar
                </p>
              </div>
            )} */}

            <div className='flex flex-col sm:flex-row gap-3 sm:justify-between'>
              <Button type='button' variant='outline' onClick={onCancel} disabled={isSubmitting}>
                ← Voltar
              </Button>
              <Button
                type='submit'
                onClick={form.handleSubmit(handleFormSubmit)}
                disabled={isSubmitting || isLoading || (isEdit && !hasFormChanges)}
                title={isEdit && !hasFormChanges ? 'Nenhuma alteração foi feita' : ''}
              >
                {isEdit ? 'Revisar Alterações' : 'Revisar Processo'} →
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Confirmação com BaseModal */}
      <BaseModal
        isOpen={showConfirmModal}
        onClose={handleCancelConfirm}
        title={`Revisar ${isEdit ? 'Alterações' : 'Processo'}`}
        description={`Confira as informações antes de ${isEdit ? 'salvar' : 'criar'}`}
        icon={<RiFilePaper2Line />}
        size='xl'
        resizable={true}
        useStandardLayout={true}
        stickyFooter={true}
        footerContent={
          <div className='flex gap-3 justify-end p-4'>
            <Button
              type='button'
              variant='outline'
              onClick={handleCancelConfirm}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type='button'
              onClick={handleConfirmSubmit}
              disabled={isSubmitting}
              className='bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
            >
              {isSubmitting ? (
                <span className='flex items-center gap-2'>
                  <svg className='animate-spin w-4 h-4' fill='none' viewBox='0 0 24 24'>
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                    ></circle>
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    ></path>
                  </svg>
                  {isEdit ? 'Salvando...' : 'Criando...'}
                </span>
              ) : isEdit ? (
                'Confirmar Alterações'
              ) : (
                'Confirmar e Criar'
              )}
            </Button>
          </div>
        }
      >
        {showConfirmModal && pendingData && (
          <div className='space-y-6'>
            {pendingData.nome_processo || selectedSetor ? (
              <div className='bg-white dark:bg-neutral-800 rounded-xl shadow-sm border border-gray-200 dark:border-neutral-700 p-6 space-y-4'>
                {/* Nome do Processo */}
                <div className='text-center space-y-2'>
                  <div className='inline-flex p-3 rounded-full bg-blue-100 dark:bg-blue-900/30 mb-2'>
                    <RiFilePaper2Line className='w-6 h-6 text-blue-600 dark:text-blue-400' />
                  </div>
                  <h4 className='text-xl font-bold text-gray-900 dark:text-gray-100'>
                    {nomeProcesso || 'Nome do Processo'}
                  </h4>
                  {selectedSetor && (
                    <div className='flex items-center justify-center gap-2 text-sm'>
                      <HiOutlineBuildingOffice className='w-4 h-4 text-gray-400' />
                      <span className='text-blue-600 dark:text-blue-400 font-semibold'>
                        {selectedSetor.nome_setor}
                      </span>
                    </div>
                  )}
                </div>

                {/* Prazos */}
                <div className='space-y-3 pt-4 border-t border-gray-100 dark:border-neutral-700'>
                  <h5 className='font-semibold text-gray-700 dark:text-gray-300 text-center'>
                    Prazos Configurados
                  </h5>

                  <div className='grid grid-cols-2 gap-2 text-sm'>
                    <div className='flex items-center justify-between p-2 bg-blue-50 rounded'>
                      <div className='flex items-center gap-2'>
                        <div className='w-2 h-2 bg-blue-500 rounded-full'></div>
                        <span>Baixa</span>
                      </div>
                      <span className='font-semibold'>
                        {(prioridadeBaixa || 8).toString()} dias
                      </span>
                    </div>

                    <div className='flex items-center justify-between p-2 bg-yellow-50 rounded'>
                      <div className='flex items-center gap-2'>
                        <div className='w-2 h-2 bg-yellow-500 rounded-full'></div>
                        <span>Média</span>
                      </div>
                      <span className='font-semibold'>
                        {(prioridadeMedia || 6).toString()} dias
                      </span>
                    </div>

                    <div className='flex items-center justify-between p-2 bg-orange-50 rounded'>
                      <div className='flex items-center gap-2'>
                        <div className='w-2 h-2 bg-orange-500 rounded-full'></div>
                        <span>Alta</span>
                      </div>
                      <span className='font-semibold'>{(prioridadeAlta || 4).toString()} dias</span>
                    </div>

                    <div className='flex items-center justify-between p-2 bg-red-50 rounded'>
                      <div className='flex items-center gap-2'>
                        <div className='w-2 h-2 bg-red-500 rounded-full'></div>
                        <span>Urgente</span>
                      </div>
                      <span className='font-semibold'>
                        {(prioridadeUrgente || 2).toString()} dias
                      </span>
                    </div>
                  </div>
                </div>

                {/* Descrição */}
                {descricao && (
                  <div className='pt-3 border-t border-gray-100 dark:border-neutral-700'>
                    <h5 className='font-semibold text-gray-700 dark:text-gray-300 mb-2'>
                      Descrição
                    </h5>
                    <div className='text-sm text-gray-600 dark:text-gray-400 leading-relaxed rich-text-content'>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: processRichTextForDisplay(descricao),
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Instruções */}
                {instrucoes && instrucoes.trim() && (
                  <div className='pt-3 border-t border-gray-100 dark:border-neutral-700'>
                    <h5 className='font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2'>
                      <FaBook className='w-3 h-3' />
                      Instruções
                    </h5>
                    <div className='text-sm text-gray-600 dark:text-gray-400 leading-relaxed rich-text-content'>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: processRichTextForDisplay(instrucoes),
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Links */}
                {linksSelecionados.length > 0 && (
                  <div className='pt-3 border-t border-gray-100 dark:border-neutral-700'>
                    <h5 className='font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2'>
                      <RiLinksLine className='w-3 h-3' />
                      Links ({linksSelecionados.length})
                    </h5>
                    <div className='space-y-1'>
                      {linksSelecionados.slice(0, 3).map((linkObj, index) => (
                        <a
                          key={index}
                          href={linkObj.link}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='text-xs text-blue-600 dark:text-blue-400 hover:underline block truncate'
                          title={linkObj.link}
                        >
                          {linkObj.link}
                        </a>
                      ))}
                      {linksSelecionados.length > 3 && (
                        <p className='text-xs text-gray-500 dark:text-gray-400 italic'>
                          +{linksSelecionados.length - 3} mais...
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Anexos */}
                {anexos.length > 0 && (
                  <div className='pt-3 border-t border-gray-100 dark:border-neutral-700'>
                    <h5 className='font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2'>
                      <RiAttachmentLine className='w-3 h-3' />
                      Anexos ({anexos.length})
                    </h5>
                    <div className='space-y-1.5'>
                      <p className='text-xs text-gray-600 dark:text-gray-400 mb-2'>
                        Total:{' '}
                        {(anexos.reduce((acc, file) => acc + file.size, 0) / (1024 * 1024)).toFixed(
                          2
                        )}{' '}
                        MB
                      </p>
                      <div className='space-y-1 max-h-32 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-neutral-600 scrollbar-track-transparent'>
                        {anexos.map((file, index) => {
                          const fileSizeMB = file.size / (1024 * 1024)
                          const isLarge = fileSizeMB > 10

                          return (
                            <div
                              key={`preview-${file.name}-${index}`}
                              className={`flex items-center justify-between p-2 rounded ${
                                isLarge
                                  ? 'bg-red-50 dark:bg-red-900/20'
                                  : 'bg-purple-50 dark:bg-purple-900/20'
                              }`}
                            >
                              <div className='flex items-center gap-2 flex-1 min-w-0'>
                                <RiAttachmentLine
                                  className={`w-3 h-3 flex-shrink-0 ${
                                    isLarge
                                      ? 'text-red-600 dark:text-red-400'
                                      : 'text-purple-600 dark:text-purple-400'
                                  }`}
                                />
                                <div className='min-w-0 flex-1'>
                                  <p
                                    className='text-xs font-medium text-gray-900 dark:text-gray-100 truncate'
                                    title={file.name}
                                  >
                                    {file.name}
                                  </p>
                                  <p
                                    className={`text-xs ${
                                      isLarge
                                        ? 'text-red-600 dark:text-red-400'
                                        : 'text-gray-500 dark:text-gray-400'
                                    }`}
                                  >
                                    {fileSizeMB >= 1
                                      ? `${fileSizeMB.toFixed(2)} MB`
                                      : `${(file.size / 1024).toFixed(2)} KB`}
                                    {isLarge && ' ⚠️'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                      {anexos.some(f => f.size > 10 * 1024 * 1024) && (
                        <p className='text-xs text-red-600 dark:text-red-400 mt-2'>
                          ⚠️ Alguns arquivos excedem 10MB
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Anexos a serem removidos */}
                {isEdit && anexosToRemove.length > 0 && (
                  <div className='pt-3 border-t border-gray-100 dark:border-neutral-700'>
                    <h5 className='font-semibold text-red-700 dark:text-red-300 mb-2 flex items-center gap-2'>
                      <RiDeleteBin6Line className='w-3 h-3' />
                      Anexos que serão removidos ({anexosToRemove.length})
                    </h5>
                    <div className='space-y-1 max-h-24 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-neutral-600 scrollbar-track-transparent'>
                      {anexosToRemove.map((path, index) => {
                        const anexoOriginal = initialData?.anexos?.find(
                          (a: AnexoProcesso) => a.anexo === path
                        )
                        return (
                          <div
                            key={index}
                            className='flex items-center gap-2 text-xs text-red-600 dark:text-red-400'
                          >
                            <RiDeleteBin6Line className='w-3 h-3 flex-shrink-0' />
                            <span className='truncate'>{anexoOriginal?.nome_anexo || 'Anexo'}</span>
                          </div>
                        )
                      })}
                    </div>
                    <p className='text-xs text-red-600 dark:text-red-400 mt-2'>
                      ⚠️ Estes arquivos serão permanentemente removidos
                    </p>
                  </div>
                )}

                {/* Autorizadores */}
                {requerAutorizacao && autorizacoesSelecionadas.length > 0 && (
                  <div className='pt-3 border-t border-gray-100 dark:border-neutral-700'>
                    <h5 className='font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2'>
                      <FaUserShield className='w-3 h-3' />
                      Autorizadores
                    </h5>
                    <div className='space-y-1'>
                      <p className='text-xs text-gray-600 dark:text-gray-400 mb-1'>
                        {autorizacaoSequencial ? 'Sequencial' : 'Paralelo'} -{' '}
                        {autorizacoesSelecionadas.length} autorizador(es)
                      </p>
                      {autorizacoesSelecionadas
                        .sort((a, b) =>
                          autorizacaoSequencial ? (a.ordem || 0) - (b.ordem || 0) : 0
                        )
                        .slice(0, 3)
                        .map(aut => {
                          const autorizador = getAutorizadorById(aut.autorizador_fk)
                          if (!autorizador) return null
                          return (
                            <div
                              key={aut.autorizador_fk}
                              className='text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1'
                            >
                              {autorizacaoSequencial && (
                                <span className='font-semibold'>{aut.ordem}.</span>
                              )}
                              <span>{autorizador.nome_setor}</span>
                            </div>
                          )
                        })}
                      {autorizacoesSelecionadas.length > 3 && (
                        <p className='text-xs text-gray-500 dark:text-gray-400 italic'>
                          +{autorizacoesSelecionadas.length - 3} mais...
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Status */}
                <div className='bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-3 mt-4'>
                  <p className='text-sm text-blue-800 dark:text-blue-300 text-center'>
                    <strong>Este processo será usado na criação de demandas</strong>
                    <br />
                    <span className='text-blue-700 dark:text-blue-400'>
                      Os prazos serão calculados automaticamente com base na prioridade.
                    </span>
                  </p>
                </div>
              </div>
            ) : (
              <div className='text-center py-8'>
                <div className='bg-gray-100 dark:bg-neutral-700 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4'>
                  <RiFilePaper2Line className='w-8 h-8 text-gray-400' />
                </div>
                <p className='text-gray-500 dark:text-gray-400 text-sm'>
                  Preencha o formulário para ver o resumo do processo
                </p>
              </div>
            )}
          </div>
        )}
      </BaseModal>
    </>
  )
}
